import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, DatePicker, Spinner } from "@heroui/react";
import { parseDate } from "@internationalized/date";
import {
  BarChart as BarChartIcon,
  PieChart,
  Utensils,
  Calendar,
  Truck,
  DollarSign,
  Package,
  Users,
  FileText,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart as RechartsLineChart,
  Line,
  Pie,
  Cell,
} from "recharts";

import { reportsService } from "@/services/reportsService";
import { formatCurrency } from "@/utils/formatters";
import { dashboardService } from "@/services/dashboardService";

// Colores para los gráficos
const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

export default function ReportsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<string>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
    // 30 días atrás
  );
  const [endDate, setEndDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [salesData, setSalesData] = useState<any>(null);
  const [revenueData, setRevenueData] = useState<any>(null);
  const [topProducts, setTopProducts] = useState<any[]>([]);

  useEffect(() => {
    fetchReportData();
  }, [startDate, endDate]);

  const fetchReportData = async () => {
    try {
      setLoading(true);

      // Parámetros para los informes
      const dateParams = {
        start_date: startDate,
        end_date: endDate,
      };

      // Cargar reporte de ventas
      const salesReport = await reportsService.getSalesReport({
        ...dateParams,
        group_by: "day",
      });

      setSalesData(salesReport);

      // Cargar reporte de ingresos
      const revenueReport = await reportsService.getRevenueReport({
        period: "monthly",
      });

      setRevenueData(revenueReport);

      // Cargar los productos más vendidos
      const responseTopProducts = await dashboardService.getTopProducts(30, 5);

      setTopProducts(responseTopProducts || []);
    } catch (err) {
      console.error("Error al cargar los datos del informe:", err);
    } finally {
      setLoading(false);
    }
  };

  const navigateToReport = (path: string) => {
    navigate(path);
  };

  return (
    <div className="p-4 space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h1 className="text-2xl font-bold">Reports and Analysis</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <DatePicker
          label="Start Date"
          value={parseDate(startDate)}
          onChange={(date) => setStartDate(date?.toString() || "")}
        />
        <DatePicker
          label="End Date"
          value={parseDate(endDate)}
          onChange={(date) => setEndDate(date?.toString() || "")}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Tarjetas de informes disponibles */}
        <Card
          className="p-6 hover:shadow-md transition-shadow cursor-pointer"
          onPress={() => navigateToReport("/reports/sales")}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-100 rounded-full">
              <BarChartIcon className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Sales Report</h2>
              <p className="text-sm text-default-500">
                Detailed sales analysis by period
              </p>
            </div>
          </div>
        </Card>

        <Card
          className="p-6 hover:shadow-md transition-shadow cursor-pointer"
          onPress={() => navigateToReport("/reports/items")}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-success-100 rounded-full">
              <Utensils className="w-6 h-6 text-success-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Top Selling Products</h2>
              <p className="text-sm text-default-500">
                View top-selling products and their performance
              </p>
            </div>
          </div>
        </Card>

        <Card
          className="p-6 hover:shadow-md transition-shadow cursor-pointer"
          onPress={() => navigateToReport("/reports/delivery")}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-warning-100 rounded-full">
              <Truck className="w-6 h-6 text-warning-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Delivery Report</h2>
              <p className="text-sm text-default-500">
                Analyze delivery performance and drivers
              </p>
            </div>
          </div>
        </Card>

        <Card
          className="p-6 hover:shadow-md transition-shadow cursor-pointer"
          onPress={() => navigateToReport("/reports/operational-costs")}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-danger-100 rounded-full">
              <DollarSign className="w-6 h-6 text-danger-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Operational Costs</h2>
              <p className="text-sm text-default-500">
                Track expenses and operational costs
              </p>
            </div>
          </div>
        </Card>

        <Card
          className="p-6 hover:shadow-md transition-shadow cursor-pointer"
          onPress={() => navigateToReport("/reports/inventory")}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-secondary-100 rounded-full">
              <Package className="w-6 h-6 text-secondary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Inventory Usage</h2>
              <p className="text-sm text-default-500">
                Inventory usage and turnover analysis
              </p>
            </div>
          </div>
        </Card>

        <Card
          className="p-6 hover:shadow-md transition-shadow cursor-pointer"
          onPress={() => navigateToReport("/reports/customers")}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-100 rounded-full">
              <Users className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Customer Analysis</h2>
              <p className="text-sm text-default-500">
                Customer behavior and loyalty analysis
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        {loading ? (
          <div className="col-span-1 lg:col-span-2 flex justify-center p-12">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            {/* Gráfico de ventas */}
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Daily Sales</h2>
                <Button
                  color="primary"
                  variant="light"
                  onPress={() => navigateToReport("/reports/sales")}
                >
                  View Full Report
                </Button>
              </div>
              <div className="h-80">
                {salesData && salesData.daily_sales ? (
                  <ResponsiveContainer height="100%" width="100%">
                    <RechartsLineChart
                      data={salesData.daily_sales}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip
                        formatter={(value) => formatCurrency(value as number)}
                      />
                      <Legend />
                      <Line
                        activeDot={{ r: 8 }}
                        dataKey="total_sales"
                        name="Sales"
                        stroke="#8884d8"
                        type="monotone"
                      />
                      <Line
                        dataKey="order_count"
                        name="Orders"
                        stroke="#82ca9d"
                        type="monotone"
                        yAxisId={1}
                      />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex justify-center items-center h-full">
                    <p className="text-default-500">No data available</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Gráfico de productos top */}
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  Top Selling Products
                </h2>
                <Button
                  color="primary"
                  variant="light"
                  onPress={() => navigateToReport("/reports/items")}
                >
                  View Full Report
                </Button>
              </div>
              <div className="h-80">
                {topProducts && topProducts.length > 0 ? (
                  <ResponsiveContainer height="100%" width="100%">
                    <BarChart
                      data={topProducts}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 60,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        angle={-45}
                        dataKey="name"
                        height={80}
                        textAnchor="end"
                      />
                      <YAxis />
                      <Tooltip
                        formatter={(value) => formatCurrency(value as number)}
                      />
                      <Legend />
                      <Bar dataKey="revenue" name="Revenue" fill="#8884d8" />
                      <Bar
                        dataKey="quantity_sold"
                        name="Quantity"
                        fill="#82ca9d"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex justify-center items-center h-full">
                    <p className="text-default-500">No data available</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Gráfico de distribución de ventas por tipo */}
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  Sales by Order Type
                </h2>
                <Button
                  color="primary"
                  variant="light"
                  onPress={() => navigateToReport("/reports/sales")}
                >
                  View Full Report
                </Button>
              </div>
              <div className="h-80">
                {salesData && salesData.sales_by_order_type ? (
                  <ResponsiveContainer height="100%" width="100%">
                    <PieChart>
                      <Pie
                        cx="50%"
                        cy="50%"
                        data={[
                          {
                            name: "Delivery",
                            value: salesData.sales_by_order_type.delivery || 0,
                          },
                          {
                            name: "Pickup",
                            value: salesData.sales_by_order_type.pickup || 0,
                          },
                        ]}
                        dataKey="value"
                        fill="#8884d8"
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                        outerRadius={100}
                      >
                        {[
                          {
                            name: "Delivery",
                            value: salesData.sales_by_order_type.delivery || 0,
                          },
                          {
                            name: "Pickup",
                            value: salesData.sales_by_order_type.pickup || 0,
                          },
                        ].map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => formatCurrency(value as number)}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex justify-center items-center h-full">
                    <p className="text-default-500">No data available</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Gráfico de ingresos mensuales */}
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Monthly Revenue</h2>
                <Button
                  color="primary"
                  variant="light"
                  onPress={() => navigateToReport("/reports/revenue")}
                >
                  View Full Report
                </Button>
              </div>
              <div className="h-80">
                {revenueData && revenueData.monthly_data ? (
                  <ResponsiveContainer height="100%" width="100%">
                    <BarChart
                      data={revenueData.monthly_data}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip
                        formatter={(value) => formatCurrency(value as number)}
                      />
                      <Legend />
                      <Bar dataKey="revenue" name="Revenue" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex justify-center items-center h-full">
                    <p className="text-default-500">No data available</p>
                  </div>
                )}
              </div>
            </Card>
          </>
        )}
      </div>

      <div className="mt-8">
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Scheduled Reports</h2>
          <div className="flex items-center justify-center p-6 border border-dashed rounded-lg">
            <div className="text-center">
              <Calendar className="w-10 h-10 text-default-300 mx-auto mb-2" />
              <p className="text-default-500">No scheduled reports</p>
              <Button
                className="mt-4"
                color="primary"
                size="sm"
                startContent={<FileText size={16} />}
                onPress={() =>
                  alert("This feature will be implemented soon")
                }
              >
                Schedule Report
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
